// src/admin/screens/loans/actions/RecommendDefaultModal.tsx
// Writes an admin_action_requests row (action_type='write_off_loan') for
// super-admin review. Marking a loan default triggers write-off and credit
// bureau reporting downstream, so like restructure it's a two-step flow.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { Json } from '@/lib/database.types';

const schema = z.object({ reason: z.string().min(10, 'At least 10 characters') });
type Values = z.infer<typeof schema>;

// Only needs id + loan_number; keeps the modal reusable from both the
// loan detail screen (which has the full LoanDetail) and the
// collections case detail screen (which assembles its own shape).
export interface RecommendDefaultLoan {
  id: string;
  loan_number: string;
}

interface Props {
  loan: RecommendDefaultLoan;
  onClose: (refresh: boolean) => void;
}

export default function RecommendDefaultModal({ loan, onClose }: Props) {
  const { admin } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const submit = async (v: Values) => {
    if (!admin) return;
    setBusy(true);
    try {
      const { error } = await supabase.from('admin_action_requests').insert({
        action_type: 'write_off_loan',
        status: 'pending',
        target_loan_id: loan.id,
        request_payload: {} as Json,
        requested_by: admin.id,
        request_notes: v.reason,
      });
      if (error) throw error;
      await recordAudit({
        action: 'loan.recommend_default',
        target_type: 'loan',
        target_id: loan.id,
        payload: { reason: v.reason },
      });
      toast({ kind: 'info', msg: 'Default recommendation submitted.' });
      onClose(false);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Recommend ${loan.loan_number} for default`}
    >
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          Marking a loan as default writes off the outstanding balance and
          reports to the credit bureau. A super admin must approve.
        </p>
        <FormField
          label="Reason"
          htmlFor="rsn"
          error={errors.reason?.message}
          required
        >
          <textarea id="rsn" rows={4} {...register('reason')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={busy}>
            Recommend default
          </Button>
        </div>
      </form>
    </Modal>
  );
}
